import { useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  type ProductInferredFact,
  useUpdateProductInferredFact,
} from '@/shared/hooks/api/product'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

type ProductInferredFactEditDialogProps = {
  productId: string
  fact: ProductInferredFact | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ProductInferredFactEditDialog({
  productId,
  fact,
  open,
  onOpenChange,
}: ProductInferredFactEditDialogProps) {
  const { t } = useTranslation('productCatalog')
  const { mutateAsync: updateInferredFact, isPending } =
    useUpdateProductInferredFact()
  const [label, setLabel] = useState('')
  const [value, setValue] = useState('')

  useEffect(() => {
    if (!open) {
      return
    }
    if (fact == null) {
      setLabel('')
      setValue('')
      return
    }
    setLabel(fact.label)
    setValue(fact.value)
  }, [open, fact])

  const normalizedLabel = label.trim()
  const normalizedValue = value.trim()

  const isUnchanged = useMemo(() => {
    if (fact == null) {
      return true
    }
    return (
      normalizedLabel === fact.label.trim() &&
      normalizedValue === fact.value.trim()
    )
  }, [fact, normalizedLabel, normalizedValue])

  const isInvalid = normalizedLabel.length === 0 || normalizedValue.length === 0
  const disableSubmit = isPending || isInvalid || isUnchanged || fact == null

  let submitLabel = t('detail.inferredFacts.editDialog.submit')
  if (isPending) {
    submitLabel = t('detail.inferredFacts.editDialog.submitting')
  }

  async function onSubmit() {
    if (fact == null) {
      return
    }

    if (normalizedLabel.length === 0 || normalizedValue.length === 0) {
      toast.error(t('detail.inferredFacts.editDialog.validationError'))
      return
    }

    try {
      await updateInferredFact({
        productId,
        factId: fact.id,
        label: normalizedLabel,
        value: normalizedValue,
      })
      toast.success(t('detail.inferredFacts.editDialog.saveSuccess'))
      onOpenChange(false)
    } catch {
      toast.error(t('detail.inferredFacts.editDialog.updateError'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>{t('detail.inferredFacts.editDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('detail.inferredFacts.editDialog.description')}
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-4'>
          <div className='space-y-2'>
            <Label htmlFor='inferred-fact-label'>
              {t('detail.inferredFacts.fields.label')}
            </Label>
            <Input
              id='inferred-fact-label'
              autoComplete='off'
              value={label}
              onChange={(event) => setLabel(event.target.value)}
            />
          </div>
          <div className='space-y-2'>
            <Label htmlFor='inferred-fact-value'>
              {t('detail.inferredFacts.fields.value')}
            </Label>
            <Textarea
              id='inferred-fact-value'
              rows={4}
              value={value}
              onChange={(event) => setValue(event.target.value)}
            />
          </div>
        </div>
        <DialogFooter className='gap-2 sm:gap-0'>
          <Button
            type='button'
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {t('dialog.cancel')}
          </Button>
          <Button type='button' onClick={onSubmit} disabled={disableSubmit}>
            {isPending && <Loader2 className='me-2 size-4 animate-spin' />}
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
